import { listPrivateDirPaths, listPrivateFilePaths } from '@bangle.io/supabase';
import { atom } from 'jotai';

const EMPTY_PRIVATE_PATHS: readonly string[] = [];

export interface PrivatePathsState {
  /** Workspace the markers belong to; readers ignore any other workspace. */
  workspaceId: string | null;
  /** Workspace-relative paths explicitly marked private (files and folders). */
  paths: readonly string[];
}

/**
 * Private-path markers for the workspace currently shown in the sidebar.
 * Tagged with its workspace so a switch never flashes another workspace's
 * lock badges while the next read is in flight.
 */
export const $privatePaths = atom<PrivatePathsState>({
  paths: EMPTY_PRIVATE_PATHS,
  workspaceId: null,
});

/** Normalizes a marker to the row form the file tree matches against. */
export function normalizePrivatePath(path: string): string {
  return path.replace(/^\/+/, '').replace(/\/+$/, '');
}

/**
 * Reads the private file and folder markers of a cloud workspace.
 *
 * Folder privacy cascades to everything beneath the folder, which consumers
 * resolve with a prefix match instead of expanding the list here: the folder's
 * own marker stays authoritative, so unhiding the folder also unhides its rows
 * without a second read.
 */
export async function fetchPrivatePaths(
  workspaceId: string,
  abortSignal?: AbortSignal,
): Promise<readonly string[]> {
  const [filePaths, dirPaths] = await Promise.all([
    listPrivateFilePaths(workspaceId, abortSignal),
    listPrivateDirPaths(workspaceId, abortSignal),
  ]);
  abortSignal?.throwIfAborted();

  return [
    ...new Set(
      [...filePaths, ...dirPaths].map((path) => normalizePrivatePath(path)),
    ),
  ];
}
